import { concat } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import { createCompareRedirectUrl } from 'lumen/utils/strategy/detail-view-helpers';

interface HotelStrategyTablesCellsTdStrategyBaserateSignature {
  Args: {
    cellData: unknown;
    column: unknown;
  };
}

export default class HotelStrategyTablesCellsTdStrategyBaserate extends Component<HotelStrategyTablesCellsTdStrategyBaserateSignature> {
  @service declare ajax: Services['ajax'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: Services['router'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get redirectUrl() {
    const { currentRouteName } = this.router;
    // @ts-expect-error: Incorrect type
    const { columnKey, subtypeAttributes } = this.args.column;

    return createCompareRedirectUrl(
      this.ajax.host,
      currentRouteName,
      columnKey,
      subtypeAttributes,
      this.rsSelections.shouldRedirectToSecondaryCompset,
      this.hotelAndMarketService,
    );
  }

  <template>
    {{! @glint-nocheck: not typesafe yet }}
    <div class="text-200--regular flex items-center justify-center gap-100">
      {{#if @cellData.isNonComputable}}
        <a
          data-event-id="hotel-rate-strategy-non-computable-icon-clicked"
          href={{concat
            "https://help."
            DOMAIN_NAME
            "/en/articles/5376608-what-does-this-icon-mean-in-rate-strategy"
          }}
          rel="noopener noreferrer"
          target="_blank"
        >
          <GeneralIconWithTooltip
            @iconName="warning"
            @side="top"
            @size="small"
            @tooltipContent={{t "strategy.warning.cannot_be_calculated"}}
          />
        </a>
      {{else}}
        {{#if @column.isRedirectDisabled}}
          <span class="line-clamp-2">{{@cellData.formattedValue}}</span>
        {{else}}
          <a
            class="link line-clamp-2"
            data-event-id="hotel-rate-strategy-detail-rates-redirect"
            href={{this.redirectUrl}}
            rel="noopener noreferrer"
            target="_blank"
          >
            {{@cellData.formattedValue}}
          </a>
        {{/if}}

        {{#each @cellData.baserate.rateTypeIcons as |icon|}}
          {{#if icon}}
            <PrismIcon @name={{icon}} @size="extra-small" />
          {{/if}}
        {{/each}}

        {{#unless (or @cellData.hasNoData @cellData.hasNoTooltip)}}
          <EmberTooltip
            @tooltipClass="base-tooltip rates-table-tooltip display-over-modal"
          >
            <div class="rates-table-tooltip-container">
              <table cellspacing="0">
                <thead>
                  <tr>
                    <th>{{t "rates.rate"}}</th>
                    <th>{{t "general.room_name"}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{@cellData.formattedValue}}</td>
                    <td>{{@cellData.baserate.roomname}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </EmberTooltip>
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
